/**
 * Onboarding wizard (mockups/auth/onboarding.html). Industry-neutral chrome (Amendment A):
 * industry examples, follow-up questions and assistant names come from template DATA
 * (`/v1/industry-templates`, `/v1/setup-goals`), never from industry-specific keys here.
 */
export const onboarding = {
  title: "Workspace setup · {{product}}",
  setupOf: "Workspace setup ·",
  stepCount: "Step {{current}} of {{total}}",
  saveExit: "Save & exit",
  saved: "Progress saved — finish setup anytime from the dashboard",
  stepsLabel: "Setup steps",
  stepDone: "(completed)",
  steps: {
    organization: "Organization",
    knowledge: "Add knowledge",
    configure: "Configure",
    test: "Test it",
    live: "Go live",
  },
  back: "Back",
  skip: "Skip setup",
  industries: {
    education: "Education (schools & universities)",
    healthcare: "Healthcare & clinics",
    real_estate: "Real estate",
    hospitality: "Hospitality & resorts",
    human_resources: "Human resources / internal help desk",
    government: "Government & public services",
    ecommerce: "E-commerce & retail",
    business: "Business & professional services",
    other: "Other",
  },
  org: {
    regionLegend: "Country, currency and time",
    country: "Country",
    currency: "Billing currency",
    currencyHint: "Philippines: pesos via GCash, Maya or card. Elsewhere: US dollars via card or PayPal.",
    timezone: "Time zone",
    language: "Default language",
    heading: "Tell us about your organization",
    subtitle: "Your answers pick a starting setup. You can change everything later in Settings.",
    industry: "What kind of organization is this?",
    industryRequired: "Choose an industry",
    industryOther: "Your industry",
    industryOtherHint: "e.g. Logistics, Non-profit, Law firm",
    industryOtherRequired: "Tell us your industry",
    goals: "What should the assistant do?",
    goalsHint: "Pick all that apply — they shape the recommended setup.",
    goalsError: "Goals couldn't load. You can continue and choose later in Settings.",
    name: "Organization name",
    website: "Website URL",
    websiteHint: "We'll suggest pages to crawl in the next step.",
    websiteInvalid: "Enter a domain like example.com",
    city: "City",
    address: "Workspace address",
    addressHint: "People open your assistant at this address.",
    slugTaken: "That address is taken. Try another.",
    slugReserved: "That address is reserved. Try another.",
    slugAvailable: "Available",
    slugChecking: "Checking…",
    slugInvalid: "Use 3–40 lowercase letters, numbers or dashes",
    details: "A few details",
    fieldRequired: "Answer this question",
    logo: "Logo",
    logoUpload: "Upload logo",
    logoReplace: "Replace logo",
    logoRemove: "Remove",
    logoHint: "PNG, JPEG or WebP, square, up to 2 MB",
    logoPending: "Uploads when you continue",
    logoUploaded: "Logo uploaded",
    logoFailed: "The logo couldn't be uploaded. Try again or add it later in Settings.",
    logoBadType: "Upload a PNG, JPEG or WebP image",
    logoTooLarge: "That image is too large",
    logoAlt: "{{name}} logo",
    describe: "Describe what you want (optional)",
    describeHint: "Who will use the assistant, what it should answer and what should happen next. We treat this as information, not instructions.",
    describeCount: "{{count}} / 1,000",
    generate: "Generate my setup",
    generateHint: "Drafts the assistant, questions, audiences and lead pipeline for you to review.",
    templatesHeading: "Start from a template",
    templatesSubtitle: "Templates pre-fill the assistant's instructions, questions, audiences and lead pipeline.",
    templatesLabel: "Starting template",
    recommended: "Recommended",
    templatesError: "Templates couldn't load. You can continue with a blank assistant.",
    templatesNeedIndustry: "Choose your industry first — templates are matched to it.",
    templateChosen: "You chose “{{chosen}}”. “{{recommended}}” is the best match for your answers.",
    useRecommended: "Use recommended",
    yourChoice: "Your choice",
    setsUp: "What “{{template}}” sets up",
    setsUpAssistant: "Assistant",
    setsUpStarters: "Starter questions",
    setsUpAudiences: "Audiences served",
    setsUpPipeline: "Lead pipeline",
    setsUpActions: "Actions",
    setsUpFootnote: "You can review and edit all of this in the Configure step.",
    actionOn: "On",
    actionOff: "Off",
    yourOrganization: "your organization",
    templateLoading: "Loading the “{{template}}” template details…",
    templateLoaded: "“{{template}}” template details loaded.",
    templateDetailsError: "We couldn't load this template's details.",
    retry: "Try again",
    detailsHint: "These questions follow the template you chose: {{template}}.",
    next: "Next: Add knowledge",
    created: "Workspace created",
  },
  knowledge: {
    heading: "Add your knowledge",
    subtitle: "The assistant only answers from what you add here, and cites the exact page. Start with your most-asked documents.",
    examples: "Good first sources: {{examples}}",
    tabFiles: "Upload files",
    tabCrawl: "Crawl website",
    tabQa: "Write Q&A",
    dropTitle: "Drag files here or browse",
    dropHint: "PDF, DOCX, XLSX, PPTX, CSV, TXT, images (OCR) · max 50 MB each",
    filesNote: "Files are stored encrypted in your workspace only and are never used to train public AI models. Remove {{sensitive}} from documents meant for the public assistant.",
    sensitiveDefault: "personal data",
    moreLater: "New sources are visible to everyone until you restrict them. You can add more sources and change who sees each one any time in the Knowledge Base.",
    waitUploads: "Waiting for uploads to finish…",
    retry: "Retry",
    retrySource: "Retry {{name}}",
    uploadsLabel: "Files being uploaded",
    sourcesHeading: "Your sources",
    sourcesLabel: "Sources added so far",
    sourcesErrorTitle: "We couldn't load your sources",
    sourcesErrorBody: "Your uploads are safe. Check your connection and try again.",
    sourcesRetry: "Try again",
    sourceFailedFallback: "This source couldn't be processed. Try again, or replace it in the Knowledge Base.",
    emptyTitle: "Nothing added yet",
    emptyBody: "Upload a document, crawl your website or write a few Q&A pairs. The assistant answers only from what you add.",
    crawlUrlRequired: "Enter your website address",
    crawlUrlInvalid: "Enter a full address like https://example.com",
    crawlFetching: "Checking site…",
    crawlFetchFailed: "We couldn't check that website",
    crawlFound_one: "{{count}} page found",
    crawlFound_other: "{{count}} pages found",
    crawlFoundCapped: "{{count}}+ pages found (limit reached)",
    crawlSelected: "{{count}} selected",
    crawlSelectAll: "Select all",
    crawlClear: "Clear",
    crawlListLabel: "Pages to add",
    crawlStartPage: "Start page, always included",
    crawlShowing: "Showing the first {{shown}} of {{total}} pages. The rest follow your include and exclude paths.",
    crawlAdd_one: "Add {{count}} page",
    crawlAdd_other: "Add {{count}} pages",
    crawlNothingTitle: "No crawlable pages found",
    crawlNothingBody: "Check the address, or loosen your include and exclude paths, then fetch again.",
    crawlTooManyExcludes: "Too many pages are switched off to send as exclusions. Switch off fewer pages, or exclude a whole folder with a path like /news/*.",
    qaListLabel: "Q&A pairs",
    qaSave: "Save Q&A",
    qaSavedBadge: "Saved",
    qaSaved_one: "{{count}} Q&A pair added to your Knowledge Base",
    qaSaved_other: "{{count}} Q&A pairs added to your Knowledge Base",
    qaIncomplete: "Some Q&A pairs have only a question or only an answer. Complete them, or remove the pair.",
    qaPartial: "{{failed}} of {{total}} Q&A pairs couldn't be saved. {{reason}}",
    crawlUrl: "Starting URL",
    crawlLimit: "Page limit",
    crawlPages: "{{count}} pages",
    crawlInclude: "Include paths",
    crawlIncludeHint: "Comma-separated. Leave blank to include everything.",
    crawlExclude: "Exclude paths",
    crawlFetch: "Fetch pages",
    crawlNote: "We respect robots.txt and re-sync weekly.",
    qaNote: "Short, exact answers for things that aren't in any document yet. Q&A pairs take priority over files.",
    question: "Question",
    answer: "Answer",
    questionPlaceholder: "e.g. {{example}}",
    questionExample: "What are your office hours?",
    answerPlaceholder: "Write the exact answer people should get…",
    removePair: "Remove",
    addPair: "Add Q&A pair",
    pairRemoved: "Q&A pair removed",
    next: "Next: Configure",
  },
  configure: {
    heading: "Configure your assistant",
    subtitle: "Review the suggested setup. Everything here is editable and nothing is saved until you confirm.",
    loading: "Preparing your setup…",
    draftError: "We couldn't prepare a setup.",
    retry: "Try again",
    sourceAi: "Drafted by AI from your description — review before saving.",
    sourceTemplate: "Based on the “{{template}}” template and your goals.",
    fallback_unavailable: "AI drafting is unavailable right now, so we used the template instead.",
    fallback_invalid: "The AI draft couldn't be used, so we used the template instead.",
    fallback_daily_cap: "Today's AI draft limit is reached, so we used the template instead.",
    describe: "Describe what you want",
    regenerate: "Generate with AI",
    name: "Assistant name",
    purpose: "Purpose template",
    purposeHint: "What this assistant is for. Switching loads that template's setup.",
    purposeRecommended: "{{name}} (recommended)",
    switching: "Loading the “{{template}}” setup…",
    switched: "Switched to the “{{template}}” setup",
    replaceTitle: "Replace your edits with the “{{template}}” setup?",
    replaceAiTitle: "Replace the AI draft with the “{{template}}” setup?",
    replaceBody: "The assistant name, instructions, questions, audiences, lead pipeline, actions, welcome message and refused topics will be replaced by this template's setup. This can't be undone.",
    replaceConfirm: "Replace with this template",
    replaceKeep: "Keep what I have",
    defaultName: "Assistant",
    instructions: "Persona & scope",
    instructionsHelp: "Grounding rules — answer only from your documents, cite sources, never invent facts — are always added by the platform and can't be removed.",
    instructionsCount: "{{count}} / 2,000",
    instructionsTooLong: "Keep this under 2,000 characters",
    starters: "Starter questions",
    starter: "Starter question {{n}}",
    audiences: "Audiences",
    audiencesHelp: "Rename the four audience tiers and choose who this assistant serves.",
    audienceLabel: "Audience {{code}} name",
    audienceServed: "Serves this audience",
    stages: "Lead pipeline",
    stagesHelp: "Keep the first stage open, mark at least one stage Won and exactly one Lost.",
    stageName: "Stage {{n}} name",
    stageKind: "Stage {{n}} type",
    stageOpen: "Open",
    stageWon: "Won",
    stageLost: "Lost",
    moveUp: "Move {{name}} up",
    moveDown: "Move {{name}} down",
    removeStage: "Remove {{name}}",
    addStage: "Add stage",
    newStage: "New stage",
    stagesInvalid: "Check the pipeline: first stage open, at least one Won, exactly one Lost, unique names.",
    actions: "Actions",
    actionsHelp: "What the assistant may do. Talking to a person is always on.",
    alwaysOn: "Always on",
    welcome: "Welcome message",
    topics: "Topics to refuse",
    topicsHint: "One topic per line.",
    preview: "Preview",
    tipLead: "Tip:",
    tipBody: "keep instructions about behavior. Put facts like prices and dates in documents so they stay cited and easy to update.",
    confirm: "Create assistant & continue",
    created: "Assistant created",
    doneTitle: "Your assistant is set up",
    doneBody: "{{name}} was created from this setup. Fine-tune it any time in Agents.",
    next: "Next: Test it",
  },
  test: {
    heading: "Test it like a visitor would",
    subtitle: "Ask a few real questions. Click a citation to see the exact passage the answer came from.",
    testMode: "Test mode · not visible to the public",
    reset: "Reset",
    tryTitle: "Try it in the Playground",
    tryBody: "Your assistant answers from the sources you add. Open the Playground to chat with it and see the full retrieval trace.",
    openPlayground: "Open Playground",
    checksTitle: "What to check",
    checkRequirements: "Answers match your source documents",
    checkLanguage: "A Taglish question gets a Taglish answer with the right facts",
    checkAction: "Booking or inquiry cards appear when someone is ready",
    checkGap: "An unknown question gets the fallback, not a guess",
    tryQuestions: "Questions to try",
    noteLead: "Wrong answer? Fix the source document or add a Q&A pair — you can do it later from",
    notePlayground: "Playground",
    noteTail: "with the full retrieval trace.",
    next: "Next: Go live",
  },
  live: {
    heading: "Go live",
    subtitle: "Choose where people can reach your assistant. You can add channels any time.",
    widget: "Website widget",
    widgetBody: "Chat bubble on {{site}}. Paste one line of code.",
    widgetBodyNoSite: "Chat bubble on your website. Paste one line of code.",
    webapp: "Web app link",
    webappBody: "A full-page chat at your own link. Great for QR codes on flyers.",
    messenger: "Facebook Messenger",
    messengerBody: "Answer messages on your Facebook Page automatically.",
    messengerNone: "No Page connected yet",
    connectPage: "Connect Page",
    connectLater: "Connect your Page from Channels after setup.",
    mobile: "Mobile app",
    mobileBody: "Embed in your own app with the iOS / Android SDK.",
    planBadge: "Standard plan",
    enabled: "{{channel}} enabled",
    disabled: "{{channel}} disabled",
    embedTitle: "Embed code",
    embedLead: "Paste before",
    embedTail: "on every page — or send it to your IT coordinator.",
    emailIt: "Email to IT",
    emailItLater: "Emailing instructions is available once the assistant is published.",
    copy: "Copy",
    copied: "Copied to clipboard",
    embedFooter: "Works with WordPress, Wix, Google Sites and plain HTML. Allowed domain:",
    agentPending: "agt_… (assigned when your assistant is published)",
    webappLink: "Web app link",
    open: "Open",
    fallbackDomain: "Fallback while DNS is set up:",
    inviteTeam: "Invite your team",
    finish: "Finish & open dashboard",
    finished: "Setup complete — welcome to your workspace",
  },
} as const;
